export const ADMIN_DATA_KEY = "maxfit:admin-data";
export const ADMIN_EVENT = "maxfit:admin-data-updated";

const trimUrl = (value) => (typeof value === "string" ? value.trim().replace(/\/+$/, "") : "");

const getPublicContentApiBases = () => {
  const fromEnv = trimUrl(process.env.REACT_APP_BACKEND_URL);
  const fromAlt = trimUrl(process.env.REACT_APP_API_ORIGIN);
  const bases = new Set();
  if (fromEnv) bases.add(`${fromEnv}/api`);
  if (fromAlt) bases.add(`${fromAlt}/api`);
  if (typeof window !== "undefined" && window.location?.origin) {
    bases.add(`${window.location.origin}/api`);
  }
  return Array.from(bases);
};

const looksLikeJson = (res) => {
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json");
};

const readLocalData = () => {
  try {
    const raw = localStorage.getItem(ADMIN_DATA_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const mergeWithLocalSafety = (incoming) => {
  const local = readLocalData();
  if (!local || typeof local !== "object") return incoming;
  const merged = { ...incoming };
  const localVersionTs = Date.parse(String(local?.dataVersion || ""));
  const incomingVersionTs = Date.parse(String(incoming?.dataVersion || ""));
  const backendIsNewer = Number.isFinite(incomingVersionTs) && (!Number.isFinite(localVersionTs) || incomingVersionTs > localVersionTs);
  const guardedLists = ["gallery", "plans", "faq", "trainers"];
  guardedLists.forEach((key) => {
    const nextRows = Array.isArray(incoming?.[key]) ? incoming[key] : [];
    const prevRows = Array.isArray(local?.[key]) ? local[key] : [];
    // Prevent wiping existing editor data when backend returns fresh/empty arrays.
    if (!backendIsNewer && nextRows.length === 0 && prevRows.length > 0) {
      merged[key] = prevRows;
    }
  });

  // Keep trainer social links from local data when backend has blank values.
  const nextTrainers = Array.isArray(incoming?.trainers) ? incoming.trainers : [];
  const prevTrainers = Array.isArray(local?.trainers) ? local.trainers : [];
  if (!backendIsNewer && nextTrainers.length && prevTrainers.length) {
    const prevById = new Map(prevTrainers.map((t) => [t?.id, t]));
    merged.trainers = nextTrainers.map((trainer) => {
      const prev = prevById.get(trainer?.id);
      if (!prev) return trainer;
      const nextSocial = trainer?.socialLinks || {};
      const prevSocial = prev?.socialLinks || {};
      const nextIg = String(nextSocial?.instagram || "").trim();
      const nextFb = String(nextSocial?.facebook || "").trim();
      const prevIg = String(prevSocial?.instagram || "").trim();
      const prevFb = String(prevSocial?.facebook || "").trim();
      if (nextIg || nextFb || (!prevIg && !prevFb)) return trainer;
      return {
        ...trainer,
        socialLinks: {
          ...nextSocial,
          instagram: prevIg,
          facebook: prevFb,
        },
      };
    });
  }
  return merged;
};

/**
 * Fetches canonical site content from the backend and updates localStorage + ADMIN_EVENT.
 * @param {{ force?: boolean }} options
 * @returns {Promise<boolean>} true if content was updated
 */
export async function syncPublicContent(options = {}) {
  if (typeof window === "undefined") return false;
  const force = options?.force === true;

  const candidates = getPublicContentApiBases();
  for (const apiBase of candidates) {
    try {
      const url = force
        ? `${apiBase}/public/content?t=${Date.now()}`
        : `${apiBase}/public/content`;
      const contentRes = await fetch(url, {
        cache: "no-store",
        credentials: "omit",
      });
      if (!contentRes.ok) continue;
      if (!looksLikeJson(contentRes)) continue;
      const payload = await contentRes.json();
      if (!payload || typeof payload !== "object") continue;
      const safePayload = mergeWithLocalSafety(payload);

      const hasUsefulContent =
        (safePayload.hero && typeof safePayload.hero === "object") ||
        (Array.isArray(safePayload.gallery) && safePayload.gallery.length > 0) ||
        (Array.isArray(safePayload.trainers) && safePayload.trainers.length > 0) ||
        (safePayload.contacts && typeof safePayload.contacts === "object");

      if (!hasUsefulContent) continue;

      localStorage.setItem(ADMIN_DATA_KEY, JSON.stringify(safePayload));
      window.dispatchEvent(new Event(ADMIN_EVENT));
      return true;
    } catch {
      // try next candidate
    }
  }
  return false;
}
